'use client';

import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["className", "index", "message", "onToolCall", "part", "slots", "slotProps"];
import * as React from 'react';
import useSlotProps from '@mui/utils/useSlotProps';
import { useMessageContentTabIndex } from "../../message-list/internals/MessageRovingContext.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
function FileIcon() {
  return /*#__PURE__*/_jsx("svg", {
    "aria-hidden": "true",
    fill: "currentColor",
    focusable: "false",
    height: "16",
    viewBox: "0 0 24 24",
    width: "16",
    style: {
      flexShrink: 0
    },
    children: /*#__PURE__*/_jsx("path", {
      d: "M6 2h8l4 4v16H6V2Zm8 1.5V7h3.5L14 3.5Z"
    })
  });
}
export const FilePart = /*#__PURE__*/React.forwardRef(function FilePart(props, ref) {
  const {
      className,
      index,
      message,
      onToolCall,
      part,
      slots,
      slotProps
    } = props,
    other = _objectWithoutPropertiesLoose(props, _excluded);
  void index;
  void onToolCall;
  const ownerState = React.useMemo(() => ({
    image: part.mediaType.startsWith('image/'),
    mediaType: part.mediaType,
    messageId: message.id,
    role: message.role
  }), [message.id, message.role, part.mediaType]);
  const Root = slots?.root ?? 'div';
  const Preview = slots?.preview ?? 'img';
  const LinkSlot = slots?.link ?? 'a';
  const Filename = slots?.filename ?? 'span';
  const rootProps = useSlotProps({
    elementType: Root,
    externalSlotProps: slotProps?.root,
    externalForwardedProps: other,
    ownerState,
    additionalProps: {
      ref,
      className
    }
  });
  const previewProps = useSlotProps({
    elementType: Preview,
    externalSlotProps: slotProps?.preview,
    ownerState
  });
  // Inside a roving message list the link leaves the tab order until the
  // user drills into the message (Enter); it stays mouse-clickable.
  const contentTabIndex = useMessageContentTabIndex();
  const linkProps = useSlotProps({
    elementType: LinkSlot,
    externalSlotProps: slotProps?.link,
    ownerState,
    additionalProps: {
      tabIndex: contentTabIndex
    }
  });
  const filenameProps = useSlotProps({
    elementType: Filename,
    externalSlotProps: slotProps?.filename,
    ownerState
  });
  return /*#__PURE__*/_jsx(Root, _extends({}, rootProps, {
    children: /*#__PURE__*/_jsxs(LinkSlot, _extends({
      href: part.url,
      rel: "noreferrer noopener",
      target: "_blank"
    }, linkProps, {
      children: [ownerState.image ? /*#__PURE__*/_jsx(Preview, _extends({
        alt: part.filename ?? '',
        src: part.url
      }, previewProps)) : /*#__PURE__*/_jsx(FileIcon, {}), /*#__PURE__*/_jsx(Filename, _extends({}, filenameProps, {
        children: part.filename ?? part.url
      }))]
    }))
  }));
});
if (process.env.NODE_ENV !== "production") FilePart.displayName = "FilePart";
export function createFilePartRenderer(defaultProps = {}) {
  return function FilePartRendererFn(rendererProps) {
    return /*#__PURE__*/_jsx(FilePart, _extends({}, defaultProps, rendererProps));
  };
}